import { useState } from 'react'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { Breadcrumb } from '@components/shared/Breadcrumb'
import { Button } from '@components/shared/Button'
import { Input } from '@components/shared/Input'
import { Title } from '@components/shared/Title'
import { emotionTheme } from '@styles/theme'
import { useMediaQuery } from '@mui/material'
import { useUser } from '@hooks/useUser'
import { PencilSimpleIcon } from '@phosphor-icons/react'
import { formattedDatePtBr } from '@utils/dates/formattedDatePtBr'
import { getAccountCodeForDisplay } from '@pages/People/helpers/accountingAccountMapping'
import {
  APDATA_CALC_TOOLTIP,
  APDATA_TOTAL_TOOLTIP,
  GroupInfoTooltip,
  INDEMNITIES_TOOLTIP
} from '@pages/People/helpers/groupTooltips'
import { usePeople } from '@pages/People/context'
import { MoneyIcon } from '@assets/svgs/MoneyIcon'
import { PaymentCardIcon } from '@assets/svgs/PaymentCardIcon'
import { BankIcon } from '@assets/svgs/BankIcon'
import { HandCoinIcon } from '@assets/svgs/HandCoinIcon'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { EditSalaryItemModal } from '@pages/People/components/modals/EditSalaryItemModal'
import * as apiPeople from '@services/api/people'
import { toast } from '@utils/functions/notificationToast'

import type { CollaboratorDetailsModalProps } from './types'

import * as S from './styles'

export const CollaboratorDetailsModal = ({ open, onClose, collaborator, canEdit = true, ...rest }: CollaboratorDetailsModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { filiData } = useUser()
  const { collaboratorDetailsData, getCollaboratorDetails, getDetailedSummary, getPeoplePreviewDetails, getCollaboratorsList } = usePeople()

  const [salariesExpanded, setSalariesExpanded] = useState(true)
  const [benefitsExpanded, setBenefitsExpanded] = useState(true)
  const [chargesExpanded, setChargesExpanded] = useState(true)
  const [indemnitiesExpanded, setIndemnitiesExpanded] = useState(true)
  
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [isUpdatingCost, setIsUpdatingCost] = useState(false)
  const [editingItem, setEditingItem] = useState<{
    name: string
    code: string
    value: number
  } | null>(null)

  if (!collaborator) return null

  // Usar dados da API se disponíveis, senão usar dados básicos do collaborator
  const detailsData = collaboratorDetailsData || {
    employeeName: collaborator.name,
    employeeRegistration: collaborator.registration,
    status: collaborator.status,
    position: collaborator.position,
    startDate: collaborator.startDate,
    salaries: { total: 0, items: [] },
    benefits: { total: 0, items: [] },
    charges: { total: 0, items: [] },
    indemnities: { total: 0, items: [] }
  }

  const getInitials = (name: string) => {
    const names = name.trim().split(' ')
    if (names.length === 1) return names[0].charAt(0).toUpperCase()
    return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativo':
        return '#34A866'
      case 'Inativo':
        return '#999'
      case 'Férias':
        return '#F5A623'
      case 'Licença Maternidade/Paternidade':
        return '#E91E63'
      default:
        return '#283897'
    }
  }

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleEditItem = (itemName: string, itemCode: string, itemValue: number) => {
    setEditingItem({
      name: itemName,
      code: itemCode,
      value: itemValue
    })
    setEditModalOpen(true)
  }

  const handleConfirmEdit = async (newValue: number) => {
    if (!editingItem || !filiData?.filiCode || !filiData?.startPeriod || !collaborator) return

    setIsUpdatingCost(true)
    try {
      const refDate = filiData.startPeriod.match(/^\d{4}-\d{2}-\d{2}$/)
        ? `${filiData.startPeriod}T00:00:00.000Z`
        : new Date(filiData.startPeriod).toISOString()

      await apiPeople.updateCollaboratorCost(
        filiData.filiCode,
        collaborator.registration,
        {
          referenceDate: refDate,
          costOrigin: 'Horas Extras',
          accountCode: editingItem.code,
          amount: newValue
        }
      )

      toast.success('Valor atualizado com sucesso!')
      setEditModalOpen(false)
      setEditingItem(null)

      await getCollaboratorDetails({
        filiCode: filiData.filiCode,
        employeeRegistration: collaborator.registration,
        referenceDate: filiData.startPeriod
      })

      await getDetailedSummary({ filiCode: filiData.filiCode, competenceDate: filiData.startPeriod })
      await getPeoplePreviewDetails({ filiCode: filiData.filiCode, competenceDate: filiData.startPeriod })
      await getCollaboratorsList({ filiCode: filiData.filiCode, referenceDate: filiData.startPeriod })
    } catch (error) {
      toast.error(
        (error as { response?: { data?: { errorMessage?: string[] } } })?.response?.data?.errorMessage?.[0] ||
          'Não foi possível atualizar o valor. Tente novamente.'
      )
    } finally {
      setIsUpdatingCost(false)
    }
  }

  return (
    <BaseModal2
      open={open}
      width={isMobile ? 'calc(100% - 20px)' : '50%'}
      heightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
      headerIcon={
        <Breadcrumb
          breadcrumbData={[
            { label: 'Pessoal' },
            { label: 'Gestão de Colaboradores' },
            { label: `Detalhes do Colaborador - ${collaborator.name}` }
          ]}
        />
      }
      onClose={onClose}
      {...rest}
    >
      <S.Container>
        <Title label="Detalhes do Colaborador" />

        <S.AlertBox>
          <S.AlertIcon>
            <WarningIcon />
          </S.AlertIcon>
          <S.AlertText>
            Ao clicar no lápis, será aberta uma janela para editar o valor. Após confirmar, o campo exibirá uma sinalização laranja durante a atualização e, ao final, a sinalização será verde e o valor não poderá mais ser editado.
          </S.AlertText>
        </S.AlertBox>

        <S.CollaboratorHeader>
          <S.CollaboratorInfo>
            <S.Avatar>{getInitials(detailsData.employeeName)}</S.Avatar>
            <S.CollaboratorDetails>
              <S.CollaboratorName>{detailsData.employeeName}</S.CollaboratorName>
              <S.CollaboratorRegistration>{detailsData.employeeRegistration}</S.CollaboratorRegistration>
            </S.CollaboratorDetails>
          </S.CollaboratorInfo>
          <S.StatusBadge color={getStatusColor(detailsData.status)}>
            {detailsData.status}
          </S.StatusBadge>
        </S.CollaboratorHeader>

        <S.FieldsRow>
          <Input
            name="position"
            label="Cargo"
            value={detailsData.position}
            disabled
            hasController={false}
          />
          <Input
            name="startDate"
            label="Data Admissão"
            value={formattedDatePtBr(detailsData.startDate)}
            disabled
            hasController={false}
          />
        </S.FieldsRow>

        {/* Salários */}
        <S.Section>
          <S.SectionHeader onClick={() => setSalariesExpanded(!salariesExpanded)}>
            <S.SectionTitleContainer>
              <S.SectionIcon color="#2B6CB0" background="#BEE3F8">
                <MoneyIcon />
              </S.SectionIcon>
              <S.SectionTitle>Salários</S.SectionTitle>
              <GroupInfoTooltip title={APDATA_TOTAL_TOOLTIP} />
            </S.SectionTitleContainer>
            <S.SectionHeaderRight>
              <S.SectionTotal>R$ {formatCurrency(detailsData.salaries.total)}</S.SectionTotal>
              <S.ItemActionSlot aria-hidden />
            </S.SectionHeaderRight>
          </S.SectionHeader>
          {salariesExpanded && (
            <S.SectionContent>
              {detailsData.salaries.items.map((item, index) => (
                <S.Item key={index}>
                  <S.ItemLeft>
                    {/* "Horas Extras" segue editável, porém sem tooltip */}
                    <S.ItemName>{item.name}</S.ItemName>
                    <S.ItemCode>{getAccountCodeForDisplay(item.name, item.accountCode)}</S.ItemCode>
                  </S.ItemLeft>
                  <S.ItemRight>
                    <S.ItemValue>R$ {formatCurrency(item.value)}</S.ItemValue>
                    <S.ItemActionSlot>
                      {item.isEditable && canEdit && (
                        <S.EditButton
                          onClick={() => item.name === 'Horas Extras' && handleEditItem(item.name, item.accountCode, item.value)}
                          disabled={item.name !== 'Horas Extras'}
                          style={{
                            opacity: item.name === 'Horas Extras' ? 1 : 0.5,
                            cursor: item.name === 'Horas Extras' ? 'pointer' : 'not-allowed'
                          }}
                        >
                          <PencilSimpleIcon size={16} weight="bold" />
                        </S.EditButton>
                      )}
                    </S.ItemActionSlot>
                  </S.ItemRight>
                </S.Item>
              ))}
            </S.SectionContent>
          )}
        </S.Section>

        {/* Benefícios */}
        <S.Section>
          <S.SectionHeader onClick={() => setBenefitsExpanded(!benefitsExpanded)}>
            <S.SectionTitleContainer>
              <S.SectionIcon color="#38A169" background="#C6F6D5">
                <PaymentCardIcon />
              </S.SectionIcon>
              <S.SectionTitle>Benefícios</S.SectionTitle>
              <GroupInfoTooltip title={APDATA_TOTAL_TOOLTIP} />
            </S.SectionTitleContainer>
            <S.SectionHeaderRight>
              <S.SectionTotal>R$ {formatCurrency(detailsData.benefits.total)}</S.SectionTotal>
              <S.ItemActionSlot aria-hidden />
            </S.SectionHeaderRight>
          </S.SectionHeader>
          {benefitsExpanded && (
            <S.SectionContent>
              {detailsData.benefits.items.map((item, index) => {
                const isNegative = item.value < 0
                return (
                  <S.Item key={index}>
                    <S.ItemLeft>
                      <S.ItemName>{item.name}</S.ItemName>
                      <S.ItemCode>{getAccountCodeForDisplay(item.name, item.accountCode)}</S.ItemCode>
                    </S.ItemLeft>
                    <S.ItemRight>
                      <S.ItemValue isNegative={isNegative}>
                        {isNegative ? '-' : ''}R$ {formatCurrency(Math.abs(item.value))}
                      </S.ItemValue>
                      <S.ItemActionSlot aria-hidden />
                    </S.ItemRight>
                  </S.Item>
                )
              })}
            </S.SectionContent>
          )}
        </S.Section>

        {/* Encargos */}
        <S.Section>
          <S.SectionHeader onClick={() => setChargesExpanded(!chargesExpanded)}>
            <S.SectionTitleContainer>
              <S.SectionIcon color="#6D7179" background="#E5E7EB">
                <BankIcon />
              </S.SectionIcon>
              <S.SectionTitle>Encargos</S.SectionTitle>
              <GroupInfoTooltip title={APDATA_CALC_TOOLTIP} />
            </S.SectionTitleContainer>
            <S.SectionHeaderRight>
              <S.SectionTotal>R$ {formatCurrency(detailsData.charges.total)}</S.SectionTotal>
              <S.ItemActionSlot aria-hidden />
            </S.SectionHeaderRight>
          </S.SectionHeader>
          {chargesExpanded && (
            <S.SectionContent>
              {detailsData.charges.items.map((item, index) => (
                <S.Item key={index}>
                  <S.ItemLeft>
                    <S.ItemName>{item.name}</S.ItemName>
                    <S.ItemCode>{getAccountCodeForDisplay(item.name, item.accountCode)}</S.ItemCode>
                  </S.ItemLeft>
                  <S.ItemRight>
                    <S.ItemValue>R$ {formatCurrency(item.value)}</S.ItemValue>
                    <S.ItemActionSlot aria-hidden />
                  </S.ItemRight>
                </S.Item>
              ))}
            </S.SectionContent>
          )}
        </S.Section>

        {/* Indenizações */}
        {detailsData.indemnities && (
          <S.Section>
            <S.SectionHeader onClick={() => setIndemnitiesExpanded(!indemnitiesExpanded)}>
              <S.SectionTitleContainer>
                <S.SectionIcon color="#5B21B6" background="#DDD6FE">
                  <HandCoinIcon />
                </S.SectionIcon>
                <S.SectionTitle>Indenizações</S.SectionTitle>
                <GroupInfoTooltip title={INDEMNITIES_TOOLTIP} />
              </S.SectionTitleContainer>
              <S.SectionHeaderRight>
                <S.SectionTotal>R$ {formatCurrency(detailsData.indemnities.total)}</S.SectionTotal>
                <S.ItemActionSlot aria-hidden />
              </S.SectionHeaderRight>
            </S.SectionHeader>
            {indemnitiesExpanded && (
              <S.SectionContent>
                {detailsData.indemnities.items.map((item, index) => (
                  <S.Item key={index}>
                    <S.ItemLeft>
                      <S.ItemName>{item.name}</S.ItemName>
                      <S.ItemCode>{getAccountCodeForDisplay(item.name, item.accountCode)}</S.ItemCode>
                    </S.ItemLeft>
                    <S.ItemRight>
                      <S.ItemValue>R$ {formatCurrency(item.value)}</S.ItemValue>
                      <S.ItemActionSlot aria-hidden />
                    </S.ItemRight>
                  </S.Item>
                ))}
              </S.SectionContent>
            )}
          </S.Section>
        )}

        <S.Footer>
          <Button
            width={190}
            colorType="outlineBlue"
            onClick={() => onClose(false)}
          >
            Voltar
          </Button>
        </S.Footer>
      </S.Container>

      <EditSalaryItemModal
        open={editModalOpen}
        onClose={setEditModalOpen}
        itemName={editingItem?.name || ''}
        itemCode={editingItem?.code || ''}
        currentValue={editingItem?.value || 0}
        onConfirm={handleConfirmEdit}
        isLoading={isUpdatingCost}
      />
    </BaseModal2>
  )
}
